<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-6">
      <h3 class="font-weight-bolder text-dark mb-0">Terminais de Venda</h3>
      <button class="btn btn-primary font-weight-bold" @click="abrirModal()">
        Novo Terminal
      </button>
    </div>

    <div v-if="loading" class="d-flex justify-content-center py-10">
      <div class="spinner spinner-primary spinner-lg"></div>
    </div>

    <div v-else-if="terminais.length === 0">
      <div class="card card-custom gutter-b">
        <div class="card-body d-flex flex-column align-items-center py-10">
          <i class="flaticon2-laptop text-muted mb-4" style="font-size: 3rem"></i>
          <h4 class="text-muted font-weight-bold mb-2">
            Nenhum terminal cadastrado
          </h4>
          <p class="text-muted mb-0">
            Clique em "Novo Terminal" para adicionar o primeiro terminal de venda.
          </p>
        </div>
      </div>
    </div>

    <div v-else class="row">
      <div
        v-for="terminal in terminais"
        :key="terminal.uuid_terminal"
        class="col-xl-4 col-lg-6 col-md-12 col-sm-12 mb-4"
      >
        <div class="card card-custom gutter-b h-100">
          <div class="card-body py-4 px-5">

            <!-- Cabeçalho: ícone + nome/uuid + status + ações -->
            <div class="d-flex align-items-start justify-content-between mb-4">
              <div class="d-flex align-items-center" style="min-width: 0">
                <div class="symbol symbol-35 symbol-light-primary mr-3 flex-shrink-0">
                  <span class="symbol-label">
                    <i class="flaticon2-laptop text-primary"></i>
                  </span>
                </div>
                <div style="min-width: 0">
                  <span class="text-dark font-weight-bolder font-size-sm d-block text-truncate">
                    {{ terminal.nome }}
                  </span>
                  <div class="d-flex align-items-center" style="min-width: 0">
                    <span
                      class="text-muted font-size-xs d-block text-truncate"
                      :title="terminal.uuid_terminal"
                    >
                      {{ terminal.uuid_terminal || "—" }}
                    </span>
                    <button
                      v-if="terminal.uuid_terminal"
                      type="button"
                      class="btn btn-clean btn-xs btn-icon ml-1 flex-shrink-0"
                      v-b-tooltip.hover.bottom="'Copiar UUID do terminal'"
                      @click="copiarUuid(terminal.uuid_terminal)"
                    >
                      <i class="far fa-copy text-muted"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div class="d-flex align-items-center flex-shrink-0 ml-2">
                <span
                  class="badge badge-sm font-size-xs mr-2"
                  :class="terminal.device_id ? 'badge-light-success' : 'badge-light-secondary'"
                >
                  {{ terminal.device_id ? 'Vinculado' : 'Disponível' }}
                </span>
                <button
                  @click="abrirModal(terminal)"
                  class="btn btn-xs btn-icon btn-info btn-hover-info mr-1"
                  v-b-tooltip.hover.bottom="'Editar'"
                >
                  <Pencil :size="14" />
                </button>
                <button
                  @click="excluir(terminal)"
                  class="btn btn-xs btn-icon btn-danger btn-hover-danger"
                  v-b-tooltip.hover.bottom="'Excluir'"
                >
                  <TrashCanIcon :size="14" />
                </button>
              </div>
            </div>

            <!-- Informações de vínculo -->
            <div class="terminal-info-grid">
              <div class="terminal-info-row">
                <span class="terminal-info-label">Computador</span>
                <span class="terminal-info-value">
                  {{ terminal.device_name || 'Nenhum' }}
                </span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Device ID</span>
                <span
                  class="terminal-info-value font-size-xs"
                  style="font-family: monospace"
                  :title="terminal.device_id || ''"
                >
                  {{ terminal.device_id ? terminal.device_id.substring(0, 18) + '…' : '—' }}
                </span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Vinculado em</span>
                <span class="terminal-info-value">{{ _formatarData(terminal.vinculado_em) }}</span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Configuração NFC-e</span>
                <span class="terminal-info-value">{{ _descricaoConfiguracaoTerminal(terminal) }}</span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Série</span>
                <span class="terminal-info-value">{{ _serieConfiguracaoTerminal(terminal) }}</span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Ambiente</span>
                <span class="terminal-info-value">{{ _ambienteConfiguracaoTerminal(terminal) }}</span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Último acesso</span>
                <span class="terminal-info-value">{{ _formatarData(terminal.ultimo_acesso_em) }}</span>
              </div>
            </div>

            <!-- Botão desvincular (só exibe se houver device_id) -->
            <div v-if="terminal.device_id" class="mt-3 pt-3 border-top">
              <button
                type="button"
                class="btn btn-sm btn-outline-danger font-weight-bold w-100"
                @click="desvincular(terminal)"
              >
                <i class="fas fa-unlink mr-1"></i>
                Desvincular computador
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Modal Novo / Editar Terminal -->
    <b-modal
      id="modal-terminal"
      :title="modalTerminal.uuid_terminal ? 'Editar Terminal' : 'Novo Terminal'"
      centered
      hide-footer
    >
      <b-form @submit.stop.prevent="salvar">
        <b-form-group label="Nome" label-for="modal-nome">
          <b-form-input
            id="modal-nome"
            v-model="modalTerminal.nome"
            :state="modalNomeState"
            placeholder="Nome do terminal"
            autofocus
          />
          <b-form-invalid-feedback>Nome é obrigatório.</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Configuração NFC-e" label-for="modal-config">
          <div v-if="!hasConfiguracaoNfceDisponivel" class="alert alert-warning mb-0">
            <p class="mb-2">
              Nenhuma configuração NFC-e modelo 65 encontrada. Cadastre uma configuração NFC-e antes de criar o terminal.
            </p>
            <button
              type="button"
              class="btn btn-sm btn-light-primary font-weight-bold"
              @click="irParaConfiguracoesNfce"
            >
              Ir para configurações NFC-e
            </button>
          </div>

          <template v-else-if="isConfiguracaoUnica">
            <div class="terminal-config-preview mt-1">
              <span>{{ configuracaoUnicaLabel }}</span>
            </div>
          </template>

          <template v-else>
            <b-form-select
              id="modal-config"
              v-model="modalTerminal.id_configuracao"
              :options="configuracoesOptions"
              :state="modalConfiguracaoState"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>
                  Selecione a configuração NFC-e do terminal
                </b-form-select-option>
              </template>
            </b-form-select>
            <b-form-invalid-feedback>
              Selecione a configuração NFC-e do terminal.
            </b-form-invalid-feedback>
            <div v-if="configuracaoSelecionada" class="terminal-config-preview mt-3">
              <span>{{ _nomeConfiguracao(configuracaoSelecionada) }}</span>
              <span>{{ _ambienteConfiguracao(configuracaoSelecionada) }}</span>
              <span>Série {{ configuracaoSelecionada.serie || '—' }}</span>
              <span>Modelo {{ configuracaoSelecionada.modelo || '—' }}</span>
            </div>
          </template>

          <small class="form-text text-muted mt-2">
            Quando houver apenas uma configuração NFC-e modelo 65, ela será usada automaticamente pelo terminal.
          </small>
        </b-form-group>

        <b-form-group v-if="modalTerminal.uuid_terminal" label="UUID do Terminal" label-for="modal-uuid">
          <b-form-input
            id="modal-uuid"
            v-model="modalTerminal.uuid_terminal"
            disabled
          />
        </b-form-group>

        <div class="d-flex justify-content-end mt-4">
          <button
            type="button"
            class="btn btn-clean font-weight-bold mr-2"
            @click="$bvModal.hide('modal-terminal')"
          >
            Cancelar
          </button>
          <button
            type="submit"
            ref="submit_modal"
            class="btn btn-primary font-weight-bold"
            :disabled="!canSalvarTerminal"
          >
            Salvar
          </button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { mapState } from "vuex";
import Pencil from "vue-material-design-icons/Pencil.vue";
import TrashCanIcon from "vue-material-design-icons/TrashCan.vue";

const emptyTerminal = () => ({ nome: "", uuid_terminal: "", id_configuracao: null });

export default {
  name: "app-terminais-index",
  components: { Pencil, TrashCanIcon },
  data() {
    return {
      terminais: [],
      configuracoes: [],
      loading: false,
      modalTerminal: emptyTerminal(),
      modalSubmetido: false,
    };
  },
  computed: {
    ...mapGetters(["itsblocked"]),
    ...mapState({
      currentUser: (state) => state.auth.user,
    }),
    modalNomeState() {
      if (!this.modalSubmetido) return null;
      return this.modalTerminal.nome.trim().length > 0 ? true : false;
    },
    modalConfiguracaoState() {
      if (!this.modalSubmetido || !this.precisaSelecionarConfiguracao) return null;
      return !!this.modalTerminal.id_configuracao;
    },
    isNovoTerminalModal() {
      return !this.modalTerminal?.uuid_terminal;
    },
    configuracoesDisponiveisModal() {
      if (!this.isNovoTerminalModal) return this.configuracoes;
      return this.configuracoes.filter((configuracao) =>
        this._isConfiguracaoDisponivelNovoTerminal(configuracao)
      );
    },
    hasConfiguracaoNfceDisponivel() {
      return this.configuracoesDisponiveisModal.length > 0;
    },
    isConfiguracaoUnica() {
      return this.configuracoesDisponiveisModal.length === 1;
    },
    precisaSelecionarConfiguracao() {
      return this.configuracoesDisponiveisModal.length > 1;
    },
    canSalvarTerminal() {
      return this.hasConfiguracaoNfceDisponivel;
    },
    configuracaoUnica() {
      return this.isConfiguracaoUnica ? this.configuracoesDisponiveisModal[0] : null;
    },
    configuracaoUnicaLabel() {
      return this.configuracaoUnica ? this._labelConfiguracao(this.configuracaoUnica) : "";
    },
    configuracoesOptions() {
      return this.configuracoesDisponiveisModal.map((c) => ({
        value: c.id ?? c.id_configuracao,
        text: this._labelConfiguracao(c),
      }));
    },
    configuracaoSelecionada() {
      const id = this.modalTerminal.id_configuracao;
      if (id === null || id === undefined || id === "") return null;
      return this.configuracoesDisponiveisModal.find(
        (c) => String(c.id ?? c.id_configuracao) === String(id)
      ) || null;
    },
  },
  methods: {
    async getConfiguracoes() {
      try {
        const { data } = await this.$http.post("/configuracoes/config-nfce");
        const configuracoes = Array.isArray(data) ? data : (data.data ?? []);
        this.configuracoes = configuracoes.filter((configuracao) =>
          this._isConfiguracaoNfceModelo65AtivaDaEmpresa(configuracao)
        );
      } catch {
        this.configuracoes = [];
      }
    },
    async getTerminais() {
      this.loading = true;
      try {
        const { data } = await this.$http.post("/terminais-venda");
        this.terminais = data;
      } catch {
        this.terminais = [];
      } finally {
        this.loading = false;
      }
    },
    async abrirModal(terminal = null) {
      await this.getConfiguracoes();
      this.modalSubmetido = false;
      const idConfiguracaoTerminal =
        terminal?.id_configuracao_nfce ??
        terminal?.id_configuracao ??
        terminal?.configuracao_nfce?.id ??
        terminal?.configuracao_nfce?.id_configuracao ??
        null;

      this.modalTerminal = terminal
        ? {
            ...terminal,
            id_configuracao: idConfiguracaoTerminal,
          }
        : emptyTerminal();

      if (this.isConfiguracaoUnica) {
        this.modalTerminal.id_configuracao =
          this.configuracaoUnica?.id ?? this.configuracaoUnica?.id_configuracao ?? null;
      }

      this.$bvModal.show("modal-terminal");
    },
    async copiarUuid(uuid) {
      if (!uuid) return;

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(uuid);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = uuid;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "absolute";
          textarea.style.left = "-9999px";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }

        this.$toast.success("UUID copiado para a area de transferencia!");
      } catch {
        this.$toast.error("Nao foi possivel copiar o UUID.");
      }
    },
    async salvar() {
      this.modalSubmetido = true;
      if (!this.modalTerminal.nome.trim()) return;
      if (!this.hasConfiguracaoNfceDisponivel) {
        this.$toast.warning(
          "Nenhuma configuração NFC-e modelo 65 encontrada. Cadastre uma configuração NFC-e antes de criar o terminal."
        );
        return;
      }

      if (this.precisaSelecionarConfiguracao && !this.modalTerminal.id_configuracao) {
        this.$toast.warning("Selecione a configuração NFC-e do terminal.");
        return;
      }

      const idConfiguracaoNfce = this.isConfiguracaoUnica
        ? this.configuracaoUnica?.id ?? this.configuracaoUnica?.id_configuracao ?? null
        : this.modalTerminal.id_configuracao || null;

      const submitButton = this.$refs["submit_modal"];
      submitButton.classList.add("spinner", "spinner-light", "spinner-right");
      submitButton.setAttribute("disabled", "");

      try {
        const url = this.modalTerminal.uuid_terminal
          ? "/terminais-venda/update"
          : "/terminais-venda/create";
        await this.$http.post(url, {
          ...this.modalTerminal,
          id_configuracao_nfce: idConfiguracaoNfce,
        });
        this.$toast.success(
          this.modalTerminal.uuid_terminal
            ? "Terminal atualizado com sucesso!"
            : "Terminal cadastrado com sucesso!"
        );
        this.$bvModal.hide("modal-terminal");
        this.getTerminais();
      } catch {
        submitButton.classList.remove("spinner", "spinner-light", "spinner-right");
        submitButton.removeAttribute("disabled");
      }
    },
    excluir(terminal) {
      if (terminal.device_id) {
        this.$swal.fire({
          icon: "error",
          title: "Terminal vinculado",
          text: `O terminal "${terminal.nome}" está vinculado ao computador "${terminal.device_name || terminal.device_id}". Desvincule o computador antes de excluir.`,
          confirmButtonText: "Entendido",
        });
        return;
      }

      this.$swal
        .fire({
          icon: "warning",
          title: "Excluir terminal?",
          text: `Deseja excluir o terminal "${terminal.nome}"?`,
          showCancelButton: true,
          confirmButtonText: "Sim, excluir",
          cancelButtonText: "Cancelar",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            try {
              await this.$http.post("/terminais-venda/delete", {
                id_terminal_venda: terminal.id_terminal_venda,
              });
              this.$toast.success("Terminal excluído com sucesso!");
              this.getTerminais();
            } catch {
              // tratado pelo interceptor do axios
            }
          }
        });
    },

    desvincular(terminal) {
      this.$swal
        .fire({
          icon: "warning",
          title: "Desvincular computador?",
          html:
            `<p>Tem certeza que deseja desvincular o terminal <strong>${terminal.nome}</strong> ` +
            `do computador <strong>${terminal.device_name || terminal.device_id}</strong>?</p>` +
            `<p class="text-muted font-size-sm mt-2">` +
            `Após isso, o PDV Desktop instalado nesse computador não poderá mais operar ` +
            `com este terminal quando estiver online, e o terminal ficará disponível ` +
            `para ser configurado em outro computador.</p>`,
          showCancelButton: true,
          confirmButtonText: "Sim, desvincular",
          confirmButtonColor: "#f64e60",
          cancelButtonText: "Cancelar",
        })
        .then(async (result) => {
          if (!result.isConfirmed) return;
          try {
            await this.$http.post(
              `/terminais-venda/${terminal.id_terminal_venda}/desvincular-device`
            );
            this.$toast.success("Terminal desvinculado com sucesso!");
            // Atualiza o terminal na lista localmente para feedback imediato
            const idx = this.terminais.findIndex(
              (t) => t.id_terminal_venda === terminal.id_terminal_venda
            );
            if (idx !== -1) {
              this.$set(this.terminais, idx, {
                ...this.terminais[idx],
                device_id:   null,
                device_name: null,
                vinculado_em: null,
              });
            }
          } catch {
            // tratado pelo interceptor do axios
          }
        });
    },

    _nomeConfiguracao(config) {
      if (!config) return "Padrão do PDV Web";
      return config.descricao || config.nome || config.nome_configuracao || `Configuração #${config.id || config.id_configuracao || "—"}`;
    },

    _labelConfiguracao(config) {
      const partes = [
        this._nomeConfiguracao(config),
        `Série ${config?.serie || "—"}`,
        this._ambienteConfiguracao(config),
      ];
      if (config?.padrao_pdv_web === true) {
        partes.push("Padrão PDV Web");
      }
      return partes.join(" — ");
    },

    _isConfiguracaoNfceModelo65AtivaDaEmpresa(configuracao) {
      const modelo = Number(configuracao?.modelo);
      const ativoRaw = configuracao?.ativo;
      const ativo = !(ativoRaw === false || ativoRaw === 0 || ativoRaw === "0");

      const idEmpresaConfig = Number(
        configuracao?.id_empresa ??
          configuracao?.empresa_id ??
          this.currentUser?.empresa?.id_empresa ??
          this.currentUser?.usuario_empresa?.id_empresa ??
          0
      );
      const idEmpresaAtual = Number(
        this.currentUser?.empresa?.id_empresa ??
          this.currentUser?.usuario_empresa?.id_empresa ??
          0
      );
      const mesmaEmpresa = idEmpresaAtual === 0 || idEmpresaConfig === 0 || idEmpresaConfig === idEmpresaAtual;

      return modelo === 65 && ativo && mesmaEmpresa;
    },

    _isConfiguracaoDisponivelNovoTerminal(configuracao) {
      return configuracao?.padrao_pdv_web !== true;
    },

    irParaConfiguracoesNfce() {
      this.$bvModal.hide("modal-terminal");
      this.$router.push({ name: "configuracoesNovo" });
    },

    _ambienteConfiguracao(config) {
      const ambiente = config?.ambiente;
      if (String(ambiente) === "1") return "Produção";
      if (String(ambiente) === "2") return "Homologação";
      return "Ambiente —";
    },

    _configuracaoDoTerminal(terminal) {
      const config = terminal?.configuracao_nfce || terminal?.configuracao || null;
      if (config) return config;
      const id = terminal?.id_configuracao_nfce ?? terminal?.id_configuracao ?? null;
      return this.configuracoes.find((c) => String(c.id) === String(id)) || null;
    },

    _descricaoConfiguracaoTerminal(terminal) {
      const config = this._configuracaoDoTerminal(terminal);
      return config ? this._nomeConfiguracao(config) : "Padrão do PDV Web";
    },

    _serieConfiguracaoTerminal(terminal) {
      const config = this._configuracaoDoTerminal(terminal);
      return config?.serie || terminal?.serie || terminal?.serie_nfe || "—";
    },

    _ambienteConfiguracaoTerminal(terminal) {
      const config = this._configuracaoDoTerminal(terminal);
      return config ? this._ambienteConfiguracao(config) : "Padrão";
    },

    _formatarData(dateStr) {
      if (!dateStr) return "—";
      try {
        return new Intl.DateTimeFormat("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(dateStr));
      } catch {
        return dateStr;
      }
    },
  },
  mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }
    this.getConfiguracoes();
    this.getTerminais();
  },
};
</script>

<style scoped>
.terminal-info-grid {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.terminal-info-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.8rem;
}

.terminal-info-label {
  color: #b5b5c3;
  white-space: nowrap;
  flex-shrink: 0;
}

.terminal-info-value {
  color: #3f4254;
  font-weight: 500;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 65%;
}

.terminal-config-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.terminal-config-preview span {
  background: #f3f6f9;
  border-radius: 4px;
  color: #3f4254;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.25rem 0.45rem;
}
</style>
